// The refund sheet: three independent questions, in order.
//
//   1. How much money goes back?
//   2. Which items, if any, came back?
//   3. What happened to those items?
//
// They are NOT linked. A £5 goodwill refund selects no items; three
// returned coffees might be refunded at £0 because the customer took a
// replacement. Nothing here derives one answer from another — the amount is
// always stated by the cashier, and the item selection exists only to drive
// inventory.
//
// Question 3 appears only once something is selected in question 2: an
// outcome with no items to apply it to is a question about nothing.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { C } from '../../../styles/theme';
import { refundService } from '../../../services/api';
import { startRefundPrint } from '../../../utils/refundPrintDispatch';
import { tableSlotKind } from '../../../utils/tableSlot';
import ManagerGate from '../ManagerGate';

// The UI's wording for each slot kind. The printed receipt keeps its own
// English literals (PrintingUtils.SLOT_LABELS) because it must match the
// backend renderers byte for byte; only the DECISION is shared. All three
// keys already exist in the translations table — nothing new to migrate.
//
// 'staff' is unreachable from this modal today: a staff allowance creates no
// transactions row, so it never appears in the refund list. Mapped anyway so
// the table is total and the next person does not have to rediscover why one
// arm is missing.
// 'table' deliberately keeps the existing `table_number` wording rather than
// the receipt's shorter "Table". A dine-in sale already displayed correctly
// here; only the order and staff arms were wrong, and restyling a correct
// label alongside a bug fix makes the diff lie about what changed.
const SLOT_LABEL_KEYS = {
    staff: (t) => t('staff'),
    order: (t) => t('order'),
    table: (t) => t('table_number'),
};

const FONT = "'Plus Jakarta Sans', sans-serif";

// Every control on a till is hit with a thumb, often a wet one. The
// category-reorder arrows shipped at 26px and were effectively untappable.
const TOUCH = 44;

const OUTCOMES = [
    ['returned', 'refund_outcome_returned'],
    ['kept_by_customer', 'refund_outcome_kept_by_customer'],
    ['binned', 'refund_outcome_binned'],
];

const money = (n) => Number(n || 0).toFixed(2);

/** How the customer actually paid, as one leg per method with its amount.
 *
 *  This is the whole point of the summary block: on a split the cashier has
 *  to know how much goes back in cash and how much to the card, and the
 *  modal previously showed neither.
 *
 *  Driven by the AMOUNTS, not by `payment_method`. The label is unreliable —
 *  a split parent reads the literal string 'Split Payment', but cash+card
 *  sales exist with `split_type` NULL and no such label, and reading the
 *  label first would show those as one leg for the full amount. The columns
 *  cannot lie: a Cash sale is written with cash_amount = final_amount, a
 *  Card sale with card_amount = final_amount (see transactionFlow.js), and a
 *  split with both populated. Whatever is non-zero is what was taken.
 *
 *  The `payment_method` fallback is for rows where neither column was
 *  written — early sales, and anything synced from a client that skipped
 *  them. Without it those show no PAID BY at all. */
export const paymentLegs = (sale) => {
    if (!sale) return [];
    const cash = Number(sale.cash_amount || 0);
    const card = Number(sale.card_amount || 0);
    if (cash > 0 || card > 0) {
        const legs = [];
        if (cash > 0) legs.push({ key: 'cash', amount: cash });
        if (card > 0) legs.push({ key: 'card', amount: card });
        return legs;
    }
    const total = Number(sale.final_amount || 0);
    const method = String(sale.payment_method || '').toLowerCase();
    if (method.includes('cash')) return [{ key: 'cash', amount: total }];
    if (method.includes('card')) return [{ key: 'card', amount: total }];
    return [];
};

/** Server errors arrive either as [{field, message}] from the rules or as a
 *  bare string from a 403/404. Mapping over a string yields one useless
 *  character per row, so both shapes are normalised here. */
const toMessages = (err) => {
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => e?.message || String(e)).filter(Boolean);
    }
    if (typeof detail === 'string' && detail) return [detail];
    return ['refund_failed'];
};

const RefundModal = ({ open, sale, lines = [], onClose, onRefunded, tenantConfig, t }) => {
    const [amount, setAmount] = useState('');
    const [selected, setSelected] = useState({});
    const [outcome, setOutcome] = useState(null);
    const [reason, setReason] = useState('');
    const [priorRefunds, setPriorRefunds] = useState([]);
    const [gateOpen, setGateOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);
    const inFlight = useRef(false);

    const transactionId = sale?.transaction_id;

    useEffect(() => {
        if (!open || !transactionId) return;
        setAmount('');
        setSelected({});
        setOutcome(null);
        setReason('');
        setErrors([]);
        setSubmitting(false);
        setGateOpen(false);
        inFlight.current = false;
        // What is still refundable depends on refunds another till may have
        // taken, so it comes from the server, never from local state.
        refundService.forTransaction(transactionId)
            .then((rows) => setPriorRefunds(rows || []))
            .catch(() => setPriorRefunds([]));
    }, [open, transactionId]);

    // Every modal in this codebase closes on Escape. Not while the request
    // is in flight, though — dismissing then would hide the outcome of a
    // refund that is already happening.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !inFlight.current && onClose) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const legs = useMemo(() => paymentLegs(sale), [sale]);

    const remaining = useMemo(() => {
        const taken = priorRefunds.reduce((sum, r) => sum + Number(r.amount || 0), 0);
        return Math.max(0, Number(sale?.final_amount || 0) - taken);
    }, [priorRefunds, sale]);

    // How many of each line are still returnable, after earlier refunds.
    const remainingQty = useMemo(() => {
        const taken = {};
        priorRefunds.forEach((r) => {
            (r.items || []).forEach((i) => {
                taken[i.line_id] = (taken[i.line_id] || 0) + Number(i.quantity || 0);
            });
        });
        const out = {};
        lines.forEach((l) => {
            out[l.line_id] = Math.max(0, Number(l.quantity || 0) - (taken[l.line_id] || 0));
        });
        return out;
    }, [priorRefunds, lines]);

    const childrenOf = useCallback(
        (lineId) => lines.filter((l) => l.parent_line_id === lineId),
        [lines],
    );

    const toggleLine = useCallback((line) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[line.line_id]) {
                delete next[line.line_id];
                // An add-on is not separately returnable, so it leaves with
                // its parent.
                childrenOf(line.line_id).forEach((c) => { delete next[c.line_id]; });
            } else {
                next[line.line_id] = 1;
                childrenOf(line.line_id).forEach((c) => { next[c.line_id] = 1; });
            }
            return next;
        });
    }, [childrenOf]);

    const setQty = useCallback((lineId, quantity) => {
        setSelected((prev) => {
            const cap = remainingQty[lineId] || 0;
            const clamped = Math.max(1, Math.min(cap, quantity));
            return { ...prev, [lineId]: clamped };
        });
    }, [remainingQty]);

    const selectedItems = useMemo(
        () => Object.entries(selected).map(([line_id, quantity]) => ({ line_id, quantity })),
        [selected],
    );

    const hasItems = selectedItems.length > 0;
    const amountValue = Number(amount);
    const canSubmit = amountValue > 0 && reason.trim().length > 0
        && (!hasItems || Boolean(outcome)) && !submitting;

    const submit = useCallback(async (approval) => {
        // A ref, not the `submitting` state: two approvals landing in the
        // same tick both read the old state and both fire. That is two
        // refunds for one decision — money out of the drawer nobody
        // authorised. The ref updates synchronously, so the second call
        // sees it.
        if (inFlight.current) return;
        inFlight.current = true;
        setSubmitting(true);
        setErrors([]);
        // The try wraps the REQUEST and nothing else. It used to wrap the
        // success path too, which meant anything thrown after the money had
        // already gone back — a print dispatch, an onRefunded handler —
        // landed in the catch below and told the cashier the refund had
        // FAILED. The refund row exists on the server at that point, so the
        // cashier's only reasonable response (do it again) would hand the
        // customer their money twice.
        let refund;
        try {
            refund = await refundService.create({
                transaction_id: transactionId,
                amount: Number(amount),
                reason: reason.trim(),
                items: selectedItems,
                items_outcome: hasItems ? outcome : null,
                manager_approval_id: approval?.id,
                approved_by_employee_id: approval?.approved_by_employee_id,
            });
        } catch (err) {
            setErrors(toMessages(err));
            // Only released on failure. After a success the modal is closing
            // and re-arming would just reopen the double-refund window.
            inFlight.current = false;
            setSubmitting(false);
            return;
        }
        setSubmitting(false);

        // Everything below here is provably post-commit.
        //
        // The customer's slip. Started, never awaited, and deliberately
        // AFTER the await above: `refund_id` is minted by the server and
        // does not exist until that promise resolves. Firing a print before
        // the record it prints exists is exactly how staff-meal tickets
        // ended up printing nothing.
        //
        // Not awaited because the money has already gone back — a printer
        // that is off, unassigned or slow must not hold the modal open.
        // startRefundPrint swallows everything (utils/refundPrintDispatch.js)
        // and the structure here means even a broken contract could only
        // ever cost the slip, never the refund.
        //
        // Before `onRefunded`, which unmounts this modal: the dispatch is
        // fire-and-forget so it survives the unmount either way, but an
        // `onRefunded` that throws must not cost the customer a slip.
        //
        // The catch deliberately does NOT setErrors. Whatever fails here —
        // the print, the caller's list refresh — the refund itself is done,
        // and this is also the only thing standing between a throw and an
        // unhandled rejection: `submit` is invoked from ManagerGate's
        // onApprove with no catch of its own.
        try {
            startRefundPrint(refund?.refund_id, tenantConfig);
            if (onRefunded) onRefunded(refund);
        } catch (postRefundError) {
            console.error('Refund completed; a post-refund step failed:', postRefundError);
        }
    }, [transactionId, amount, reason, selectedItems, hasItems, outcome, onRefunded, tenantConfig]);

    if (!open) return null;

    const label = {
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, color: C.muted, marginBottom: 6,
    };
    const box = {
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: 14, marginBottom: 12,
    };

    // Portaled to document.body. A `position:fixed` overlay whose ancestor
    // has a backdrop-filter/transform is contained by THAT element, not the
    // viewport — the POS is built out of glass panels, and this exact bug
    // has been diagnosed seven times in this repo. See
    // pos-app/fixedOverlayPortals.test.js.
    return createPortal(
        <>
            <div
                role="dialog"
                aria-modal="true"
                style={{
                    position: 'fixed', inset: 0, zIndex: 1000,
                    background: 'rgba(0,0,0,0.45)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', padding: 16,
                    fontFamily: FONT,
                }}
            >
                <div style={{
                    background: C.bg, borderRadius: 16, width: 'min(560px, 100%)',
                    maxHeight: '90vh', overflowY: 'auto', padding: 18,
                }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 14,
                    }}>
                        <h2 style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: 0 }}>
                            {t('refund')}
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label={t('close')}
                            style={{
                                minWidth: TOUCH, minHeight: TOUCH, borderRadius: 12,
                                border: `1px solid ${C.border}`, background: C.surface,
                                color: C.ink, fontSize: 18, cursor: 'pointer',
                            }}
                        >
                            ×
                        </button>
                    </div>

                    {/* ── 0. what is being refunded ───────────────────────
                        Deliberately first, and deliberately read-only.

                        A cashier arrives here from a list of transaction ids
                        and amounts and nothing else — no way to confirm they
                        opened the right sale, and on a split no way to know
                        how the money should be handed back.

                        The item lines repeat what "ITEMS RETURNED" below
                        already lists. That is accepted, not overlooked: the
                        two answer different questions (what was sold vs what
                        came back). They are kept small and muted so the
                        interactive section stays the obvious one to act on.

                        The modal body already scrolls (maxHeight 90vh,
                        overflowY auto above), so a long order pushes Confirm
                        Refund down the scroll, never off the screen. */}
                    <div style={{ ...box, background: C.faint }}>
                        <div style={label}>{t('refund_sale_summary')}</div>

                        <div style={{
                            display: 'flex', alignItems: 'baseline',
                            justifyContent: 'space-between', gap: 8,
                        }}>
                            <span style={{ fontWeight: 800, color: C.ink, fontSize: 15 }}>
                                {sale?.transaction_id}
                            </span>
                            <span style={{ fontWeight: 800, color: C.ink, fontSize: 15 }}>
                                {money(sale?.final_amount)}
                            </span>
                        </div>

                        <div style={{ marginTop: 4, fontSize: 12, color: C.muted }}>
                            {[
                                sale?.dining_option,
                                // Only when there is one. A takeaway has no
                                // table and "Table " with nothing after it is
                                // worse than nothing.
                                // `table_number` is overloaded — see
                                // utils/tableSlot.js. A takeaway carries the
                                // daily counter here ("#021"), and labelling
                                // that "Table Number" invents a table the
                                // sale never had. Same rule the printed
                                // receipt and both backend renderers use.
                                sale?.table_number
                                    ? `${SLOT_LABEL_KEYS[
                                        tableSlotKind(sale.table_number, sale.dining_option)
                                    ](t)} ${sale.table_number}`
                                    : null,
                                [sale?.date, sale?.time].filter(Boolean).join(' '),
                            ].filter(Boolean).join(' · ')}
                        </div>

                        {lines.length > 0 && (
                            <div style={{
                                marginTop: 10, paddingTop: 10,
                                borderTop: `1px solid ${C.border}`,
                            }}>
                                {lines.map((line) => {
                                    const isAddon = Boolean(line.parent_line_id);
                                    return (
                                        <div
                                            key={`summary-${line.line_id}`}
                                            style={{
                                                display: 'flex', gap: 8,
                                                justifyContent: 'space-between',
                                                fontSize: 12, color: C.muted,
                                                lineHeight: 1.7,
                                                marginInlineStart: isAddon ? 12 : 0,
                                            }}
                                        >
                                            <span>
                                                {isAddon ? '+ ' : `${line.quantity} × `}
                                                {line.product_name}
                                            </span>
                                            <span style={{ whiteSpace: 'nowrap' }}>
                                                {money(line.total_amount)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {legs.length > 0 && (
                            <div style={{
                                marginTop: 10, paddingTop: 10,
                                borderTop: `1px solid ${C.border}`,
                            }}>
                                <div style={label}>{t('refund_paid_by')}</div>
                                {legs.map((leg) => (
                                    <div
                                        key={leg.key}
                                        style={{
                                            display: 'flex', gap: 8,
                                            justifyContent: 'space-between',
                                            fontSize: 13, color: C.ink, lineHeight: 1.8,
                                        }}
                                    >
                                        <span>{t(leg.key)}</span>
                                        <span style={{ fontWeight: 700 }}>{money(leg.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ── 1. how much ─────────────────────────────────── */}
                    <div style={box}>
                        <div style={label}>{t('refund_amount')}</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                                name="amount"
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                style={{
                                    flex: 1, minHeight: TOUCH, borderRadius: 10,
                                    border: `1px solid ${C.border}`, padding: '0 12px',
                                    fontSize: 18, fontWeight: 700, fontFamily: FONT,
                                    background: C.white, color: C.ink,
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setAmount(money(remaining))}
                                style={{
                                    minHeight: TOUCH, padding: '0 14px', borderRadius: 10,
                                    border: `1px solid ${C.greenBorder}`,
                                    background: C.greenLight, color: C.greenDark,
                                    fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                }}
                            >
                                {t('refund_everything')}
                            </button>
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12, color: C.muted }}>
                            {t('refund_remaining')}: {money(remaining)}
                        </div>
                    </div>

                    {/* ── 2. which items came back ────────────────────── */}
                    {lines.length > 0 && (
                        <div style={box}>
                            <div style={label}>{t('refund_items_returned')}</div>
                            {lines.map((line) => {
                                const cap = remainingQty[line.line_id] || 0;
                                const isAddon = Boolean(line.parent_line_id);
                                const parentChecked = isAddon
                                    ? Boolean(selected[line.parent_line_id]) : true;
                                const checked = Boolean(selected[line.line_id]);
                                return (
                                    <div
                                        key={line.line_id}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            minHeight: TOUCH,
                                            marginInlineStart: isAddon ? 18 : 0,
                                            opacity: cap === 0 ? 0.45 : 1,
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={cap === 0 || (isAddon && !parentChecked)}
                                            onChange={() => toggleLine(line)}
                                            style={{ width: 22, height: 22 }}
                                        />
                                        <span style={{ flex: 1, color: C.ink }}>
                                            {isAddon ? '+ ' : ''}{line.product_name}
                                            <span style={{ color: C.muted, marginInlineStart: 8 }}>
                                                ×{cap}
                                            </span>
                                        </span>
                                        {checked && cap > 1 && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <button
                                                    type="button"
                                                    aria-label={t('decrease')}
                                                    onClick={() => setQty(line.line_id, selected[line.line_id] - 1)}
                                                    style={{
                                                        minWidth: TOUCH, minHeight: TOUCH,
                                                        borderRadius: 10, border: `1px solid ${C.border}`,
                                                        background: C.surface, color: C.ink,
                                                        fontSize: 18, cursor: 'pointer',
                                                    }}
                                                >
                                                    −
                                                </button>
                                                <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 700 }}>
                                                    {selected[line.line_id]}
                                                </span>
                                                <button
                                                    type="button"
                                                    aria-label={t('increase')}
                                                    onClick={() => setQty(line.line_id, selected[line.line_id] + 1)}
                                                    style={{
                                                        minWidth: TOUCH, minHeight: TOUCH,
                                                        borderRadius: 10, border: `1px solid ${C.border}`,
                                                        background: C.surface, color: C.ink,
                                                        fontSize: 18, cursor: 'pointer',
                                                    }}
                                                >
                                                    +
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ── 3. what happened to them ────────────────────── */}
                    {hasItems && (
                        <div style={box}>
                            <div style={label}>{t('refund_items_outcome')}</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {OUTCOMES.map(([value, key]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setOutcome(value)}
                                        style={{
                                            flex: '1 1 30%', minHeight: TOUCH, borderRadius: 10,
                                            border: `1px solid ${outcome === value ? C.greenBorder : C.border}`,
                                            background: outcome === value ? C.greenLight : C.surface,
                                            color: outcome === value ? C.greenDark : C.ink,
                                            fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                        }}
                                    >
                                        {t(key)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── reason ──────────────────────────────────────── */}
                    <div style={box}>
                        <div style={label}>{t('refund_reason')}</div>
                        <textarea
                            name="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            style={{
                                width: '100%', borderRadius: 10, padding: 10,
                                border: `1px solid ${C.border}`, fontFamily: FONT,
                                fontSize: 14, resize: 'vertical', background: C.white,
                                color: C.ink,
                            }}
                        />
                    </div>

                    {errors.length > 0 && (
                        <div style={{
                            background: C.redLight, border: `1px solid ${C.red}`,
                            borderRadius: 10, padding: 10, marginBottom: 12,
                            color: C.red, fontSize: 13,
                        }}>
                            {errors.map((message, i) => <div key={i}>{message}</div>)}
                        </div>
                    )}

                    <button
                        type="button"
                        disabled={!canSubmit}
                        onClick={() => setGateOpen(true)}
                        style={{
                            width: '100%', minHeight: 52, borderRadius: 12, border: 'none',
                            background: canSubmit ? C.red : C.border,
                            color: canSubmit ? C.white : C.muted,
                            fontSize: 16, fontWeight: 800, fontFamily: FONT,
                            cursor: canSubmit ? 'pointer' : 'not-allowed',
                        }}
                    >
                        {t('confirm_refund')}
                    </button>
                </div>
            </div>

            <ManagerGate
                open={gateOpen}
                action="refund"
                context={{ transaction_id: transactionId, amount: Number(amount) }}
                onApprove={(approval) => {
                    setGateOpen(false);
                    submit(approval);
                }}
                onCancel={() => setGateOpen(false)}
            />
        </>,
        document.body,
    );
};

export default RefundModal;
